/**
 * VOICEMAIL EVIDENCE — plan-031 VMD-DOM (design §42.4, §42.5; ADR 0085, proposed; glossary §6.46 *Voicemail signal*,
 * *Listening window*, *Late window*).
 *
 * What the runtime's per-leg detector (`apps/runtime/src/voicemail/**`) collects while it listens at the answer of an
 * outbound conversation, as data the pure rule (`./verdict.ts`) decides from. Every signal is a code with the time it was
 * seen (milliseconds since the admission, the meter's start) and the stage it was seen in; a phrase signal names the
 * pack entry's stable key, never the words heard (DD31-8, OD-6).
 *
 * | Signal | Source | Strength |
 * |---|---|---|
 * | `beep` | the beep detector | certain |
 * | `announcement_phrase` | a `voicemail.announcement` entry in the first utterance | certain |
 * | `voicemail_phrase` | a `voicemail.phrase` entry | certain |
 * | `voicemail_word` | a `voicemail.word` entry | weak (certain only with `long_speech`) |
 * | `long_speech` | continuous first speech ≥ `voicemail_detection.long_speech_ms` | weak |
 * | `person_phrase` | the whole first utterance is a `voicemail.person_greeting` entry | person |
 * | `short_answer` | ≤ `person_answer_word` words reaching their endpoint within `person_answer_max_ms` | person |
 * | `silence` | nothing heard when the opening wait elapses | none — never evidence of a mailbox |
 *
 * The speech shape (`long_speech`, `short_answer`) is computed here too ({@link speechShapeSignalOf}), so the runtime's
 * detector and the voicemail detection evaluation decide from one rule. Pure: no I/O, no clock.
 */
/** Every voicemail signal, in the order of design §42.5's table. */
export const VOICEMAIL_SIGNAL = [
    "beep",
    "announcement_phrase",
    "voicemail_phrase",
    "voicemail_word",
    "long_speech",
    "person_phrase",
    "short_answer",
    "silence",
];
/** Each signal's strength (design §42.5). */
export const VOICEMAIL_SIGNAL_STRENGTH = Object.freeze({
    beep: "certain",
    announcement_phrase: "certain",
    voicemail_phrase: "certain",
    voicemail_word: "weak",
    long_speech: "weak",
    person_phrase: "person",
    short_answer: "person",
    silence: "none",
});
/** The signals that alone make a `voicemail` verdict — the only ones the late window reads at all (OD-3). */
export const CERTAIN_VOICEMAIL_SIGNAL = VOICEMAIL_SIGNAL.filter((signal) => VOICEMAIL_SIGNAL_STRENGTH[signal] === "certain");
/**
 * Where detection was listening: `listening` — from the answer until the opening (the party-first opening's window);
 * `late` — after the opening, for `voicemail_detection.late_window_ms` or until the party's first turn is answered.
 */
export const VOICEMAIL_DETECTION_STAGE = ["listening", "late"];
/** Nothing heard and nothing seen: the evidence at the answer. */
export const NO_VOICEMAIL_EVIDENCE = Object.freeze({ seen: Object.freeze([]), speechMs: null });
/**
 * The evidence with one more signal. A signal already seen with the same phrase key in the same stage is not added
 * twice (a phrase found in a partial is found again in the final), so the event's `signal[]` names each once.
 */
export function evidenceWith(evidence, seen) {
    const already = evidence.seen.some((one) => one.signal === seen.signal && one.phraseKey === seen.phraseKey && one.stage === seen.stage);
    if (already)
        return evidence;
    return { seen: [...evidence.seen, seen], speechMs: evidence.speechMs };
}
/** The evidence with the first utterance's continuous speech so far. */
export function evidenceWithSpeech(evidence, speechMs) {
    return { seen: evidence.seen, speechMs };
}
/** The first utterance's continuous speech: first text to its endpoint, or to now while it is still going. */
export function speechMsOf(shape) {
    const end = shape.endpointAtMs === null ? shape.nowMs : shape.endpointAtMs;
    return Math.max(0, end - shape.firstTextAtMs);
}
/** Words in a recogniser text: runs of letters or digits (punctuation and spacing never count). */
export function wordCountOf(text) {
    return text.match(/[\p{L}\p{N}]+/gu)?.length ?? 0;
}
/**
 * The speech-shape signals the first utterance shows now: `long_speech` once its continuous speech reaches
 * `longSpeechMs`; `short_answer` once it has reached its endpoint with at least one and at most `personAnswerWord`
 * words within `personAnswerMaxMs` of its first text. Never both.
 */
export function speechShapeSignalOf(shape, threshold) {
    const speechMs = speechMsOf(shape);
    if (speechMs >= threshold.longSpeechMs)
        return ["long_speech"];
    if (shape.endpointAtMs !== null &&
        shape.wordCount >= 1 &&
        shape.wordCount <= threshold.personAnswerWord &&
        speechMs <= threshold.personAnswerMaxMs) {
        return ["short_answer"];
    }
    return [];
}
//# sourceMappingURL=evidence.js.map